import React from 'react';
import { DashboardCard } from './DashboardLayout';
import { Button } from '../ui/button';
import { ChevronRight } from 'lucide-react';
import { cn } from '../ui/utils';

interface WeekDay {
  date: Date;
  hasMeals: boolean;
}

interface WeekCalendarProps {
  weekDays?: WeekDay[];
  onViewFullPlan?: () => void;
  className?: string;
}

export function WeekCalendar({ weekDays, onViewFullPlan, className }: WeekCalendarProps) {
  const today = new Date().getDay();
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Generate default week if not provided
  const defaultWeek = Array.from({ length: 7 }, (_, i) => ({
    date: new Date(Date.now() + i * 24 * 60 * 60 * 1000),
    hasMeals: false,
  }));

  const days = weekDays || defaultWeek;

  return (
    <DashboardCard
      className={cn('col-span-1 md:col-span-4 lg:col-span-4', className)}
      span={{ mobile: 1, tablet: 4, desktop: 4 }}
    >
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-dusk">This Week</h2>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1 text-coral hover:text-coral/80"
          onClick={onViewFullPlan}
        >
          View Plan
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-2">
        {days.map((day, index) => {
          const isToday = index === today;
          const dayName = dayNames[day.date.getDay()];

          return (
            <div
              key={index}
              className={cn(
                'flex flex-col items-center gap-1 rounded-lg p-2 transition-colors',
                isToday && 'bg-coral/10',
                !isToday && 'hover:bg-neutral-50'
              )}
            >
              <span className={cn(
                'text-xs font-medium',
                isToday ? 'text-coral' : 'text-supporting'
              )}>
                {dayName}
              </span>
              <div className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full text-sm',
                isToday && 'bg-coral text-white font-semibold',
                !isToday && 'text-dusk'
              )}>
                {day.date.getDate()}
              </div>
              {day.hasMeals && (
                <div className="h-1 w-1 rounded-full bg-coral" />
              )}
            </div>
          );
        })}
      </div>
    </DashboardCard>
  );
}
